import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, DollarSign, Users, MessagesSquare } from "lucide-react";

const cards = [
  {
    id: 0,
    title: "Product Reviews",
    subtitle: "Authentic reviews from real influencers",
    color: "from-slate-50 to-gray-100",
    accent: "#3B5BA5",
    icon: Star,
  },
  {
    id: 1,
    title: "Monetize Influence",
    subtitle: "Earn money or products for your opinions",
    color: "from-slate-50 to-gray-100",
    accent: "#3B5BA5",
    icon: DollarSign,
  },
  {
    id: 2,
    title: "Connect with Brands",
    subtitle: "Build relationships with matching brands",
    color: "from-slate-50 to-gray-100",
    accent: "#3B5BA5",
    icon: Users,
  },
  {
    id: 3,
    title: "Direct Communication",
    subtitle: "Message brands directly, no intermediaries",
    color: "from-slate-50 to-gray-100",
    accent: "#3B5BA5",
    icon: MessagesSquare,
  },
];

// Mock interface content for each card — mirrors actual platform UI
const CardContent = ({ cardId }) => {
  if (cardId === 0) {
    // Product Reviews — campaign card UI
    return (
      <div className="p-5">
        {/* Campaign image header */}
        <div className="relative h-24 rounded-xl overflow-hidden bg-gray-100 mb-3">
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-medium text-gray-600 bg-white/80">Fashion</span>
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium text-gray-700 bg-white/80">₱2,500</span>
        </div>
        {/* Campaign title */}
        <div className="h-3 w-3/4 rounded-full bg-gray-800 mb-2" />
        <div className="h-2 w-full rounded-full bg-gray-100 mb-1.5" />
        <div className="h-2 w-5/6 rounded-full bg-gray-100 mb-1.5" />
        <div className="h-2 w-2/3 rounded-full bg-gray-100 mb-3" />
        {/* Spots + deadline */}
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2 py-0.5 rounded-md text-[10px] font-medium text-gray-500 bg-gray-50">3 spots left</span>
          <span className="px-2 py-0.5 rounded-md text-[10px] font-medium text-gray-500 bg-gray-50">Ends Aug 30</span>
        </div>
        {/* Apply button */}
        <div className="h-8 rounded-lg bg-gray-900 flex items-center justify-center">
          <span className="text-[11px] font-semibold text-white">View Details &amp; Apply</span>
        </div>
      </div>
    );
  }
  if (cardId === 1) {
    // Monetize — Payments dashboard UI
    return (
      <div className="p-5">
        <div className="text-xs font-bold text-gray-900 mb-3">Payments</div>
        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
            <div className="text-[8px] text-gray-400 mb-1">Total Earned</div>
            <div className="text-sm font-bold text-gray-900">₱48,500</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
            <div className="text-[8px] text-gray-400 mb-1">Pending</div>
            <div className="text-sm font-bold text-gray-900">₱12,000</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
            <div className="text-[8px] text-gray-400 mb-1">Available</div>
            <div className="text-sm font-bold text-gray-900">₱36,500</div>
          </div>
        </div>
        {/* Payment history rows */}
        <div className="space-y-2">
          {[
            { name: "Summer Collection Review", status: "Completed", color: "text-gray-500" },
            { name: "Skincare Launch Campaign", status: "Processing", color: "text-gray-500" },
            { name: "Food Festival Promo", status: "Pending", color: "text-gray-400" },
          ].map((row, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[9px] font-bold text-gray-500">₱</div>
              <div className="flex-1">
                <div className="h-2 w-28 rounded-full bg-gray-700 mb-1" />
                <div className="h-1.5 w-20 rounded-full bg-gray-200" />
              </div>
              <span className={`text-[9px] font-medium ${row.color}`}>{row.status}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[9px] text-gray-400 text-center">GCash · Bank Transfer · PayPal</div>
      </div>
    );
  }
  if (cardId === 2) {
    // Connect with Brands — Browse Campaigns grid UI
    return (
      <div className="p-5">
        <div className="text-xs font-bold text-gray-900 mb-3">Browse Campaigns</div>
        {/* Filter bar */}
        <div className="flex gap-2 mb-3">
          <div className="px-2.5 py-1 rounded-md text-[10px] font-medium text-gray-600 bg-gray-50 border border-gray-100">All Categories</div>
          <div className="px-2.5 py-1 rounded-md text-[10px] font-medium text-gray-600 bg-gray-50 border border-gray-100">All Types</div>
          <div className="px-2.5 py-1 rounded-md text-[10px] font-medium text-gray-600 bg-gray-50 border border-gray-100">Sort</div>
        </div>
        {/* Campaign grid */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { cat: "Beauty", comp: "₱1,500", spots: "5 spots" },
            { cat: "Food", comp: "Product", spots: "2 left" },
            { cat: "Fitness", comp: "₱3,000", spots: "8 spots" },
          ].map((c, i) => (
            <div key={i} className="rounded-lg border border-gray-100 overflow-hidden">
              <div className="h-12 bg-gray-100 relative">
                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full text-[8px] font-medium text-gray-600 bg-white/80">{c.cat}</span>
              </div>
              <div className="p-1.5">
                <div className="h-1.5 w-full rounded-full bg-gray-800 mb-1" />
                <div className="h-1 w-3/4 rounded-full bg-gray-200 mb-1" />
                <div className="h-1 w-2/3 rounded-full bg-gray-100 mb-1.5" />
                <div className="flex gap-1">
                  <span className="px-1.5 py-0.5 rounded text-[8px] font-medium text-gray-600 bg-gray-50">{c.comp}</span>
                  <span className="px-1.5 py-0.5 rounded text-[8px] font-medium text-gray-400 bg-gray-50">{c.spots}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-center gap-1.5 mt-3">
          <div className="w-1.5 h-1.5 rounded-full bg-gray-800" />
          <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
          <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
        </div>
      </div>
    );
  }
  // Direct Communication — Messages UI
  return (
    <div className="p-5 flex gap-3 h-[200px]">
      {/* Conversation sidebar */}
      <div className="w-1/3 bg-gray-50 rounded-xl p-2 space-y-2">
        <div className="h-6 rounded-md bg-white border border-gray-100 flex items-center px-2">
          <div className="w-2 h-2 rounded-full border border-gray-400" />
          <div className="ml-1.5 h-1.5 w-12 rounded-full bg-gray-200" />
        </div>
        {[
          { active: true },
          { active: false },
          { active: false },
          { active: false },
        ].map((conv, i) => (
          <div key={i} className={`flex items-center gap-1.5 p-1.5 rounded-lg ${conv.active ? "bg-gray-100" : ""}`}>
            <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-[8px] font-bold text-gray-600">B</div>
            <div className="flex-1">
              <div className="h-1.5 w-14 rounded-full bg-gray-700 mb-0.5" />
              <div className="h-1 w-10 rounded-full bg-gray-300" />
            </div>
            {conv.active && <div className="w-1.5 h-1.5 rounded-full bg-gray-800" />}
          </div>
        ))}
      </div>
      {/* Chat area */}
      <div className="flex-1 flex flex-col">
        {/* Chat header */}
        <div className="flex items-center gap-2 pb-2 mb-2 border-b border-gray-100">
          <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-[9px] font-bold text-gray-600">B</div>
          <div className="h-2 w-16 rounded-full bg-gray-800" />
          <div className="h-1.5 w-8 rounded-full bg-gray-300 ml-auto" />
        </div>
        {/* Messages */}
        <div className="flex-1 space-y-2">
          <div className="flex">
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3 py-2 max-w-[75%]">
              <div className="h-1.5 w-16 rounded-full bg-gray-400 mb-1" />
              <div className="h-1.5 w-12 rounded-full bg-gray-300" />
            </div>
          </div>
          <div className="flex justify-end">
            <div className="bg-gray-800 rounded-2xl rounded-br-sm px-3 py-2 max-w-[75%]">
              <div className="h-1.5 w-20 rounded-full bg-white/70 mb-1" />
              <div className="h-1.5 w-16 rounded-full bg-white/50" />
            </div>
          </div>
          <div className="flex">
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3 py-2 max-w-[60%]">
              <div className="h-1.5 w-12 rounded-full bg-gray-400" />
            </div>
          </div>
        </div>
        {/* Input bar */}
        <div className="mt-2 h-7 rounded-full bg-gray-50 border border-gray-100 flex items-center px-3">
          <div className="h-1.5 w-20 rounded-full bg-gray-200" />
          <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center ml-auto">
            <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function FluxCardHero() {
  const [activeCard, setActiveCard] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveCard((prev) => (prev + 1) % cards.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative min-h-[90vh] w-full overflow-hidden bg-white">
      {/* Background gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, #eff6ff 0%, #f5f3ff 35%, #fefce8 70%, #ffffff 100%)",
        }}
      />
      {/* Low-opacity wave layers */}
      <svg
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="none"
        viewBox="0 0 1440 800"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 400 C 240 320, 480 480, 720 400 C 960 320, 1200 480, 1440 400 L1440 800 L0 800 Z"
          fill="#3B5BA5"
          fillOpacity="0.05"
        />
        <path
          d="M0 480 C 240 420, 480 540, 720 480 C 960 420, 1200 540, 1440 480 L1440 800 L0 800 Z"
          fill="#8b5cf6"
          fillOpacity="0.05"
        />
        <path
          d="M0 560 C 240 510, 480 590, 720 560 C 960 510, 1200 590, 1440 560 L1440 800 L0 800 Z"
          fill="#f59e0b"
          fillOpacity="0.04"
        />
      </svg>
      {/* Decorative blurred blobs */}
      <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-blue-200/20 blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-violet-200/20 blur-3xl" />
      <div className="absolute top-1/2 left-1/2 w-80 h-80 rounded-full bg-amber-100/20 blur-3xl" />

      {/* Hero content + card stack */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 pt-32 pb-20">
        {/* Top: centered text + single CTA */}
        <div className="text-center max-w-2xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-gray-900 leading-[1.1]"
          >
            Connect, Review,{' '}
            <span className="text-primary">Get Rewarded</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-5 text-lg text-gray-500 max-w-md mx-auto"
          >
            The premier platform connecting brands with influencers for authentic product reviews.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 flex items-center justify-center"
          >
            <a
              href="/campaigns#active-campaigns"
              className="inline-flex items-center justify-center px-7 py-3.5 bg-primary hover:bg-primary-dark text-white font-semibold rounded-full shadow-lg transition-colors duration-300 cursor-pointer"
            >
              Live Campaigns
            </a>
          </motion.div>
        </div>

        {/* Bottom: animated card stack */}
        <div className="relative h-[420px] flex items-center justify-center mt-10">
          <AnimatePresence mode="popLayout">
            {cards.map((card, i) => {
              const isActive = i === activeCard;
              const offset = i - activeCard;
              const Icon = card.icon;

              return (
                <motion.div
                  key={card.id}
                  initial={{
                    scale: 0.8,
                    opacity: 0,
                    y: 40,
                    rotate: 0,
                  }}
                  animate={{
                    scale: isActive ? 1 : 0.85 - Math.abs(offset) * 0.05,
                    opacity: Math.abs(offset) > 2 ? 0 : 1 - Math.abs(offset) * 0.15,
                    y: offset * 20,
                    x: offset * -40,
                    rotate: offset * 4,
                    zIndex: 10 - Math.abs(offset),
                  }}
                  exit={{
                    scale: 0.7,
                    opacity: 0,
                    y: -40,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  }}
                  className="absolute w-[640px] max-w-full cursor-pointer"
                  onClick={() => setActiveCard(i)}
                  style={{ pointerEvents: Math.abs(offset) > 2 ? "none" : "auto" }}
                >
                  {/* Card */}
                  <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                    {/* Card header with gradient */}
                    <div className={`bg-gradient-to-r ${card.color} p-5 text-gray-900 border-b border-gray-200`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gray-900/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-gray-700" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-base text-gray-900">{card.title}</h3>
                          <p className="text-xs text-gray-500">{card.subtitle}</p>
                        </div>
                      </div>
                    </div>
                    {/* Card body — interface mockup */}
                    <div className="bg-white">
                      <CardContent cardId={card.id} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
        </div>

        {/* Navigation dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {cards.map((card, i) => (
            <button
              key={card.id}
              onClick={() => setActiveCard(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === activeCard
                  ? "w-8 bg-primary"
                  : "w-2 bg-gray-300 hover:bg-gray-400"
              }`}
              aria-label={`View ${card.title}`}
            />
          ))}
        </div>
      </div>
  );
}
